import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { ChatAttachment, ChatMessage } from '../../models/chat.models';

/** Distance from bottom (px) within which the view is considered "at end". */
const SCROLL_THRESHOLD = 60;

@Component({
  selector: 'app-chat-conversation',
  templateUrl: './chat-conversation.component.html',
  styleUrls: ['./chat-conversation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatConversationComponent implements AfterViewChecked {
  @Input() messages: ReadonlyArray<ChatMessage> = [];
  @Output() readonly suggestionPick = new EventEmitter<string>();
  @Output() readonly retry = new EventEmitter<{ text: string; attachments?: ChatAttachment[] }>();

  @ViewChild('scrollRegion', { static: false })
  private readonly scrollRegion?: ElementRef<HTMLDivElement>;

  private previousLength = 0;
  private previousStreamingContent = '';
  private userScrolledUp = false;
  showNewMessageBadge = false;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  private get scrollEl(): HTMLDivElement | undefined {
    return this.scrollRegion?.nativeElement;
  }

  private isNearBottom(): boolean {
    const el = this.scrollEl;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD;
  }

  /** Called by the scroll event on #scrollRegion */
  onScroll(): void {
    const wasScrolledUp = this.userScrolledUp;
    this.userScrolledUp = !this.isNearBottom();
    if (wasScrolledUp && !this.userScrolledUp) {
      // User scrolled back to bottom — dismiss badge
      this.showNewMessageBadge = false;
      this.cdr.markForCheck();
    }
  }

  /** Called when the user taps the "↓ nuevo mensaje" badge */
  scrollToBottomFromBadge(): void {
    const el = this.scrollEl;
    if (!el) return;
    this.userScrolledUp = false;
    this.showNewMessageBadge = false;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    this.cdr.markForCheck();
  }

  ngAfterViewChecked(): void {
    const el = this.scrollEl;
    if (!el) return;

    const currentLength = this.messages.length;

    // Track streaming: the last message content changes on every delta while isStreaming
    const lastMsg = this.messages[currentLength - 1];
    const streamingContent = lastMsg?.isStreaming ? (lastMsg.content ?? '') : '';

    const lengthChanged = currentLength !== this.previousLength;
    const streamingChanged = streamingContent !== this.previousStreamingContent;

    if (!lengthChanged && !streamingChanged) return;

    this.previousLength = currentLength;
    this.previousStreamingContent = streamingContent;

    if (this.userScrolledUp && lengthChanged) {
      // A brand-new message arrived while the user is reading history — show badge
      this.showNewMessageBadge = true;
      this.cdr.markForCheck();
      return;
    }

    // Instant scroll (no smooth) to keep pace with streaming tokens
    if (!this.userScrolledUp) {
      el.scrollTop = el.scrollHeight;
    }
  }

  trackById(_index: number, message: ChatMessage): string { return message.id; }
}
